// Catalogue de prix : bibliothèque de tarifs fournisseurs (prix datés et sourcés, dernière version par fournisseur, historique,
// comparaison), taux horaires, ouvrages types, import d'un tarif fournisseur (aperçu avant import, imports précédents).
import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, BookOpen, Clock, FileDown, FileUp, History, Pencil, Plus, Search, Settings2, Upload } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { date, dateHeure, pluriel } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Champ, ChampChoix, Chargement, Dialogue, EntetePage, EtatErreur, EtatVide, FilContexte, Onglets, Panneau, useNotifier,
} from '../../components/ui.tsx';
import { Montant, saisieValide, useEtroit, versSaisie } from './commun.tsx';
import { OngletOuvrages } from './ouvrages.tsx';
import { DetailPrix, DialogueComparaison, texteProvenance, type PrixCatalogue } from './tarifs.tsx';
import { dateDuJourParis } from '@shared/dates.ts';
import { CODES_FAMILLES, FAMILLES_SURPLUS, horsRecommandation, type FamilleSurplus } from '@shared/surplus.ts';
import { useModules } from '../../lib/modules.ts';
import { OngletConditions, OngletSurveillance, RemisesImport, type RemiseNegociee } from './tarification.tsx';

type Onglet = 'prix' | 'taux' | 'ouvrages' | 'import' | 'surveiller' | 'conditions';
interface Fournisseur { id: string; name: string; nb_references: number; dernier_tarif: string | null }

export function PageCatalogue() {
  const { peut } = useSession();
  const [onglet, setOnglet] = useState<Onglet>(() => {
    // Lien direct « ?onglet=ouvrages » (depuis un devis reçu, un dialogue d'insertion) ; sinon le dernier onglet consulté.
    const demande = new URLSearchParams(window.location.search).get('onglet');
    if (demande === 'prix' || demande === 'taux' || demande === 'ouvrages' || demande === 'import' || demande === 'surveiller' || demande === 'conditions') return demande;
    try { return (sessionStorage.getItem('trame-catalogue-onglet') as Onglet) || 'prix'; } catch { return 'prix'; }
  });
  useEffect(() => { try { sessionStorage.setItem('trame-catalogue-onglet', onglet); } catch { /* sans stockage : pas grave */ } }, [onglet]);
  // Option tarification avancée (activée par l'éditeur) : onglets absents tant qu'elle est inactive.
  const avance = !!useModules().tarification_avancee && peut('devis.couts');
  const onglets: { id: Onglet; libelle: string }[] = [{ id: 'prix', libelle: 'Prix' },
    ...(avance ? [{ id: 'surveiller' as const, libelle: 'Prix à surveiller' }] : []), { id: 'taux', libelle: 'Taux horaires' },
    { id: 'ouvrages', libelle: 'Ouvrages types' }, ...(peut('prix.ecrire') ? [{ id: 'import' as const, libelle: 'Import fournisseur' }] : []),
    ...(avance ? [{ id: 'conditions' as const, libelle: 'Conditions fournisseurs' }] : [])];
  const actif: Onglet = onglets.some((o) => o.id === onglet) ? onglet : 'prix';
  return (
    <div className="pile-l devis-page">
      <EntetePage fil={<FilContexte noeuds={[{ type: '', libelle: 'Études et devis', to: '/etudes' }, { type: '', libelle: 'Catalogue de prix' }]} />}
        titre="Catalogue de prix" sousTitre="Tarifs des fournisseurs datés et sourcés, taux horaires de main-d’œuvre, ouvrages types, import des tarifs." />
      {!peut('devis.couts') && <Alerte titre="Prix d’achat réservés">Votre rôle affiche les références et désignations ; les prix d’achat et les coûts horaires sont réservés aux rôles habilités.</Alerte>}
      <Onglets libelle="Catalogue" onglets={onglets} actif={actif} changer={setOnglet} />
      {actif === 'prix' && <OngletPrix />}
      {actif === 'surveiller' && <OngletSurveillance />}
      {actif === 'taux' && <OngletTaux />}
      {actif === 'ouvrages' && <OngletOuvrages />}
      {actif === 'import' && peut('prix.ecrire') && <OngletImport aller={() => setOnglet('prix')} />}
      {actif === 'conditions' && <OngletConditions />}
    </div>
  );
}

const useFournisseurs = () => useQuery({ queryKey: ['fournisseurs'], queryFn: () => api.get<Fournisseur[]>('/api/fournisseurs') });

// ─── Prix ───
function OngletPrix() {
  const { peut } = useSession();
  const [saisie, setSaisie] = useState('');
  const [q, setQ] = useState('');
  const [fournisseur, setFournisseur] = useState('');
  const [edite, setEdite] = useState<PrixCatalogue | null | 'nouveau'>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [reglages, setReglages] = useState(false);
  useEffect(() => { const t = window.setTimeout(() => setQ(saisie.trim()), 300); return () => window.clearTimeout(t); }, [saisie]);
  const fournisseurs = useFournisseurs();
  const r = useQuery({ queryKey: ['prix', q, 'catalogue', fournisseur],
    queryFn: () => api.get<{ exacte: boolean; elements: PrixCatalogue[] }>(`/api/prix?q=${encodeURIComponent(q)}&limite=200${fournisseur ? `&fournisseur=${fournisseur}` : ''}`) });
  const seuil = useQuery({ queryKey: ['prix-reglages'], queryFn: () => api.get<{ ancienneteMois: number }>('/api/prix/reglages') });
  const couts = peut('devis.couts');
  const etroit = useEtroit(600);
  const boutonsLigne = (p: PrixCatalogue) => (
    <>
      {couts && ((p.nb_fournisseurs ?? 0) > 1 || (p.nb_versions ?? 0) > 1) && (
        <Bouton compact icone={(p.nb_fournisseurs ?? 0) > 1 ? <ArrowRightLeft aria-hidden /> : <History aria-hidden />} onClick={() => setReference(p.reference)}>
          {(p.nb_fournisseurs ?? 0) > 1 ? `Comparer (${p.nb_fournisseurs})` : `Historique (${p.nb_versions})`}</Bouton>
      )}
      {peut('prix.ecrire') && <Bouton compact variante="fantome" className="btn-icone" aria-label={`Modifier ${p.reference}`} onClick={() => setEdite(p)}><Pencil aria-hidden /></Bouton>}
    </>
  );
  return (
    <Panneau serre titre={r.data ? pluriel(r.data.elements.length, 'prix', 'prix') : 'Prix'} actions={
      <div className="devis-catalogue-outils">
        <label className="rangee" style={{ gap: 6 }}>
          <Search size={18} aria-hidden className="discret" />
          <span className="visuellement-cache">Rechercher une référence, une désignation ou un fournisseur</span>
          <input className="saisie saisie-mono devis-filtre" placeholder="Référence, désignation, fournisseur" value={saisie} onChange={(e) => setSaisie(e.target.value)} />
        </label>
        {!!fournisseurs.data?.length && (
          <select className="saisie devis-choix-fournisseur" aria-label="Fournisseur" value={fournisseur} onChange={(e) => setFournisseur(e.target.value)}>
            <option value="">Tous les fournisseurs</option>
            {fournisseurs.data.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        )}
        {peut('prix.ecrire') && <Bouton variante="principal" icone={<Plus aria-hidden />} onClick={() => setEdite('nouveau')}>Ajouter un prix</Bouton>}
        {couts && <a className="btn" href={`/api/prix/export.csv${fournisseur ? `?fournisseur=${fournisseur}` : ''}`}><FileDown aria-hidden />Exporter (CSV)</a>}
        {peut('prix.ecrire') && <Bouton icone={<Settings2 aria-hidden />} onClick={() => setReglages(true)}>Réglages</Bouton>}
      </div>}>
      {seuil.data && <p className="tres-petit discret" style={{ margin: '0 0 8px' }}>{`Dernier prix de chaque fournisseur pour chaque référence. « Prix ancien » : tarif de plus de ${seuil.data.ancienneteMois} mois.`}</p>}
      {r.isLoading && <Chargement />}
      {r.isError && <EtatErreur erreur={r.error} reessayer={() => r.refetch()} />}
      {r.data && r.data.elements.length === 0 && (
        <EtatVide icone={<BookOpen />} titre={q || fournisseur ? 'Aucun prix ne correspond' : 'Catalogue vide'}
          texte={q || fournisseur ? 'Aucune référence, désignation ni fournisseur ne correspond à la recherche.' : 'Importez le tarif d’un fournisseur ou ajoutez un prix saisi, avec sa source et sa date.'} />
      )}
      {r.data && r.data.elements.length > 0 && etroit && (
        <ul className="devis-cartes-catalogue">
          {r.data.elements.map((p) => (
            <li key={p.id} className="devis-carte-catalogue">
              <div className="pile">
                <span className="rangee" style={{ gap: 6 }}><span className="mono">{p.reference}</span>{p.ancien && <Badge ton="ambre">Prix ancien</Badge>}</span>
                <span>{p.label}</span>
                <span className="tres-petit discret">{[p.supplier_name ?? p.source_libelle, p.price_date ? `tarif du ${date(p.price_date)}` : 'Date inconnue'].filter(Boolean).join(' — ')}</span>
                {couts && <DetailPrix p={p} />}
              </div>
              <div className="devis-carte-catalogue-droite">{boutonsLigne(p)}</div>
            </li>
          ))}
        </ul>
      )}
      {r.data && r.data.elements.length > 0 && !etroit && (
        <div className="tableau-conteneur">
          <table className="tableau">
            <thead><tr>
              <th scope="col">Référence</th><th scope="col">Désignation</th><th scope="col">Fournisseur</th>
              <th scope="col">Unité</th>{couts && <th scope="col" className="num">Prix net HT</th>}<th scope="col">Date et provenance</th>
              <th scope="col"><span className="visuellement-cache">Actions</span></th>
            </tr></thead>
            <tbody>
              {r.data.elements.map((p) => (
                <tr key={p.id}>
                  <td><span className="mono">{p.reference}</span>{p.exacte && q && <> <Badge ton="succes">Exacte</Badge></>}</td>
                  <td>{p.label}{p.manufacturer && <span className="tres-petit discret"> — {p.manufacturer}</span>}</td>
                  <td>{p.supplier_name ?? <span className="petit secondaire">{p.source_libelle}</span>}</td>
                  <td>{p.unit}</td>
                  {couts && <td className="num"><DetailPrix p={p} /></td>}
                  <td className="petit">
                    <span className="rangee" style={{ gap: 6 }}>{p.price_date ? date(p.price_date) : 'Date inconnue'}{p.ancien && <Badge ton="ambre">Prix ancien</Badge>}</span>
                    <span className="tres-petit discret">{texteProvenance(p.provenance)}</span>
                  </td>
                  <td><div className="groupe-btn">{boutonsLigne(p)}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <DialoguePrix prix={edite} fermer={() => setEdite(null)} />
      <DialogueComparaison reference={reference} fermer={() => setReference(null)} />
      <DialogueReglages ouvert={reglages} fermer={() => setReglages(false)} actuel={seuil.data?.ancienneteMois ?? null} />
    </Panneau>
  );
}

function DialogueReglages({ ouvert, fermer, actuel }: { ouvert: boolean; fermer: () => void; actuel: number | null }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [mois, setMois] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => { if (ouvert) { setMois(actuel ? String(actuel) : '6'); setErreur(null); } }, [ouvert, actuel]);
  const m = useMutation({
    mutationFn: () => api.put('/api/prix/reglages', { ancienneteMois: Number(mois) }),
    onSuccess: () => { notifier('Réglage enregistré.'); void qc.invalidateQueries({ queryKey: ['prix-reglages'] }); void qc.invalidateQueries({ queryKey: ['prix'] });
      void qc.invalidateQueries({ queryKey: ['revision'] }); fermer(); },
    onError: (e) => setErreur(messageErreur(e)),
  });
  return (
    <Dialogue titre="Réglages du catalogue" ouvert={ouvert} fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" type="submit" form="form-reglages" chargement={m.isPending}>Enregistrer</Bouton></>}>
      <form id="form-reglages" className="pile" onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <Champ libelle="Alerter quand un prix d’achat a plus de (mois)" type="number" inputMode="numeric" min={1} max={60} required value={mois}
          onChange={(e) => setMois(e.target.value)} aide="De 1 à 60 mois (6 par défaut). Sert aux badges « Prix ancien » du catalogue et aux alertes des devis." />
      </form>
    </Dialogue>
  );
}

function DialoguePrix({ prix, fermer }: { prix: PrixCatalogue | null | 'nouveau'; fermer: () => void }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const fournisseurs = useFournisseurs();
  const ouvert = prix !== null;
  const ex = prix && prix !== 'nouveau' ? prix : null;
  const importe = ex?.source === 'import_fournisseur';
  const vierge = { reference: '', label: '', manufacturer: '', unit: 'u', purchasePrice: '', listPrice: '', discountPct: '', source: 'saisie', sourceLabel: '',
    priceDate: '', supplierId: '', packageQuantity: '', packageLabel: '', famille: '', tauxSurplus: '', motifSurplus: '' };
  const [f, setF] = useState(vierge);
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => {
    if (!ouvert) return;
    setErreur(null);
    setF(ex ? { reference: ex.reference, label: ex.label, manufacturer: ex.manufacturer ?? '', unit: ex.unit, purchasePrice: versSaisie(ex.purchase_price),
      listPrice: versSaisie(ex.list_price), discountPct: versSaisie(ex.discount_pct), source: ex.source, sourceLabel: ex.source_label ?? '',
      priceDate: ex.price_date ?? '', supplierId: ex.supplier_id ?? '',
      packageQuantity: versSaisie(ex.conditionnement?.quantite), packageLabel: ex.conditionnement?.libelle ?? '',
      famille: ex.surplus?.famille ?? '', tauxSurplus: versSaisie(ex.surplus?.taux), motifSurplus: ex.surplus?.source?.motif ?? '' }
      : { ...vierge, priceDate: dateDuJourParis() });
  }, [ouvert, ex]); // eslint-disable-line react-hooks/exhaustive-deps
  const m = useMutation({
    mutationFn: async () => {
      // Prix importé : seuls désignation, unité et fabricant se corrigent (le prix, sa date et sa source font foi).
      const corps: Record<string, unknown> = importe
        ? { label: f.label, manufacturer: f.manufacturer || null, unit: f.unit || 'u' }
        : { reference: f.reference, label: f.label, manufacturer: f.manufacturer || null, unit: f.unit || 'u',
            purchasePrice: f.purchasePrice.trim() === '' ? null : f.purchasePrice, listPrice: f.listPrice.trim() === '' ? null : f.listPrice,
            discountPct: f.discountPct.trim() === '' ? null : f.discountPct, sourceLabel: f.sourceLabel || null, priceDate: f.priceDate,
            source: f.source, supplierId: f.supplierId || null };
      if (!ex || f.packageQuantity !== versSaisie(ex.conditionnement?.quantite) || f.packageLabel !== (ex.conditionnement?.libelle ?? '')) {
        corps.packageQuantity = f.packageQuantity || null; corps.packageLabel = f.packageLabel || null;
      }
      const r = ex ? await api.patch(`/api/prix/${ex.id}`, corps) : await api.post('/api/prix', corps);
      // Famille « surplus » : attribut de l'article, posé sur toutes ses lignes de prix (même référence, même unité).
      const avant = { famille: ex?.surplus?.famille ?? '', taux: versSaisie(ex?.surplus?.taux), motif: ex?.surplus?.source?.motif ?? '' };
      if (f.famille !== avant.famille || f.tauxSurplus !== avant.taux || f.motifSurplus !== avant.motif) {
        const avecTaux = !!f.famille && FAMILLES_SURPLUS[f.famille as FamilleSurplus]?.surplus && f.tauxSurplus.trim() !== '';
        await api.put('/api/prix/famille-surplus', { reference: f.reference || ex?.reference, unit: f.unit || 'u', famille: f.famille || null,
          taux: avecTaux ? f.tauxSurplus.trim() : null, motif: avecTaux ? f.motifSurplus.trim() || null : null });
      }
      return r;
    },
    onSuccess: () => { notifier(ex ? 'Prix modifié.' : 'Prix ajouté.'); void qc.invalidateQueries({ queryKey: ['prix'] }); void qc.invalidateQueries({ queryKey: ['prix-reference'] }); fermer(); },
    onError: (e) => setErreur(messageErreur(e)),
  });
  const ch = (k: keyof typeof f) => ({ value: f[k], onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }) });
  const soumettre = (e: FormEvent) => {
    e.preventDefault();
    if (!importe && (!saisieValide(f.purchasePrice) || !saisieValide(f.listPrice) || !saisieValide(f.discountPct))) { setErreur('Nombre illisible (exemple : 12,50).'); return; }
    m.mutate();
  };
  return (
    <Dialogue titre={ex ? `Modifier le prix ${ex.reference}` : 'Ajouter un prix'} ouvert={ouvert} fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" type="submit" form="form-prix" chargement={m.isPending}>Enregistrer</Bouton></>}>
      <form id="form-prix" className="pile" onSubmit={soumettre}>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        {importe && ex && (
          <Alerte titre="Prix importé d’un tarif fournisseur">
            {`${ex.supplier_name ?? ex.source_libelle} — tarif du ${ex.price_date ? date(ex.price_date) : 'date inconnue'}. ${texteProvenance(ex.provenance)}. `}
            Le prix et sa date ne se modifient pas : saisissez un nouveau prix daté, ou réimportez le tarif corrigé.
          </Alerte>
        )}
        <div className="grille-2">
          <Champ libelle="Référence" mono required maxLength={120} disabled={importe} {...ch('reference')} />
          <Champ libelle="Fabricant" maxLength={120} {...ch('manufacturer')} />
          <Champ libelle="Désignation" required maxLength={500} {...ch('label')} />
          <Champ libelle="Unité" maxLength={20} {...ch('unit')} />
          <Champ libelle="Quantité par conditionnement" inputMode="decimal" {...ch('packageQuantity')} aide="Dans l’unité de l’article. Vide avec le libellé vide = conditionnement inconnu." />
          <Champ libelle="Libellé du conditionnement" maxLength={120} {...ch('packageLabel')} aide="Ex. touret 100 m, boîte de 50. Aucune quantité déduite du texte." />
          <ChampChoix libelle="Famille pour le surplus" vide="À décider (aucun surplus proposé)" value={f.famille} onChange={(e) => setF({ ...f, famille: e.target.value })}
            options={CODES_FAMILLES.map((c) => ({ valeur: c, libelle: `${FAMILLES_SURPLUS[c].libelle} — ${FAMILLES_SURPLUS[c].surplus ? `${FAMILLES_SURPLUS[c].taux} %` : 'jamais de surplus'}` }))}
            aide="S’applique à toutes les lignes de prix de cette référence (même unité)." />
          {f.famille && FAMILLES_SURPLUS[f.famille as FamilleSurplus]?.surplus && <>
            <Champ libelle="Taux propre à l’article (%)" inputMode="decimal" {...ch('tauxSurplus')} placeholder={FAMILLES_SURPLUS[f.famille as FamilleSurplus].taux ?? ''}
              aide="Vide = taux de la famille. Recommandé : 15 à 20 % ; possible : 0 à 30 %." />
            {horsRecommandation(f.tauxSurplus.trim() && saisieValide(f.tauxSurplus) ? f.tauxSurplus.replace(',', '.') : null) &&
              <Champ libelle="Raison du taux hors 15 à 20 %" maxLength={500} {...ch('motifSurplus')} />}
          </>}
          {ex?.conditionnement && <p className="petit secondaire">Source du conditionnement : {ex.conditionnement.source.fichier ? `fichier « ${ex.conditionnement.source.fichier} », ligne ${ex.conditionnement.source.ligne}` : 'Saisie'} — {ex.conditionnement.source.par ?? 'auteur inconnu'} {ex.conditionnement.source.le ? dateHeure(ex.conditionnement.source.le) : ''}</p>}
          {!importe && <>
            <ChampChoix libelle="Fournisseur" vide="Non précisé" value={f.supplierId} onChange={(e) => setF({ ...f, supplierId: e.target.value })}
              options={(fournisseurs.data ?? []).map((x) => ({ valeur: x.id, libelle: x.name }))}
              aide={fournisseurs.data?.length ? undefined : 'Les fournisseurs se créent à l’import d’un tarif.'} />
            <Champ libelle="Prix d’achat net HT (€)" inputMode="decimal" {...ch('purchasePrice')} aide="Vide = inconnu (« À renseigner »), jamais zéro." />
            <Champ libelle="Prix brut HT (€)" inputMode="decimal" {...ch('listPrice')} aide="Facultatif : tarif public avant remise." />
            <Champ libelle="Remise (%)" inputMode="decimal" {...ch('discountPct')} aide="Facultatif : de 0 à 99,99." />
            <ChampChoix libelle="Origine" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })}
              options={[{ valeur: 'saisie', libelle: 'Saisie' }, { valeur: 'catalogue', libelle: 'Catalogue autorisé' }]} />
            <Champ libelle="Précision sur la source" maxLength={200} {...ch('sourceLabel')} aide="Ex. devis fournisseur n°…, catalogue constructeur 2026" />
            <Champ libelle="Date du prix" type="date" required {...ch('priceDate')} />
          </>}
        </div>
      </form>
    </Dialogue>
  );
}

// ─── Taux horaires ───
interface Taux { id: string; code: string; label: string; hourly_cost?: string | null; hourly_price?: string | null; active: boolean }

function OngletTaux() {
  const { peut } = useSession();
  const [edite, setEdite] = useState<Taux | null | 'nouveau'>(null);
  const r = useQuery({ queryKey: ['taux-horaires'], queryFn: () => api.get<Taux[]>('/api/taux-horaires') });
  const couts = peut('devis.couts');
  const etroit = useEtroit(600);
  return (
    <Panneau serre titre="Taux horaires" actions={peut('prix.ecrire') && <Bouton variante="principal" icone={<Plus aria-hidden />} onClick={() => setEdite('nouveau')}>Ajouter un taux</Bouton>}>
      {r.isLoading && <Chargement />}
      {r.isError && <EtatErreur erreur={r.error} reessayer={() => r.refetch()} />}
      {r.data && r.data.length === 0 && <EtatVide icone={<Clock />} titre="Aucun taux horaire"
        texte="Créez les taux de main-d’œuvre (technicien, programmeur, mise en service…) : sans taux, les heures d’une ligne restent « À renseigner »." />}
      {r.data && r.data.length > 0 && etroit && (
        <ul className="devis-cartes-catalogue">
          {r.data.map((t) => (
            <li key={t.id} className="devis-carte-catalogue">
              <div className="pile">
                <span><span className="mono">{t.code}</span> — {t.label}</span>
                {couts && <span className="petit secondaire">Coût <Montant v={t.hourly_cost ?? null} /> — vente indicative <Montant v={t.hourly_price ?? null} /></span>}
              </div>
              <div className="devis-carte-catalogue-droite">
                {t.active ? <Badge ton="succes">Actif</Badge> : <Badge>Inactif</Badge>}
                {peut('prix.ecrire') && <Bouton compact icone={<Pencil aria-hidden />} onClick={() => setEdite(t)}>Modifier</Bouton>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {r.data && r.data.length > 0 && !etroit && (
        <div className="tableau-conteneur">
          <table className="tableau">
            <thead><tr><th scope="col">Code</th><th scope="col">Libellé</th>
              {couts && <th scope="col" className="num">Coût horaire de revient</th>}{couts && <th scope="col" className="num">Taux de vente indicatif</th>}
              <th scope="col">État</th>{peut('prix.ecrire') && <th scope="col"><span className="visuellement-cache">Actions</span></th>}</tr></thead>
            <tbody>
              {r.data.map((t) => (
                <tr key={t.id}>
                  <td className="mono">{t.code}</td><td>{t.label}</td>
                  {couts && <td className="num"><Montant v={t.hourly_cost ?? null} /></td>}
                  {couts && <td className="num"><Montant v={t.hourly_price ?? null} /></td>}
                  <td>{t.active ? <Badge ton="succes">Actif</Badge> : <Badge>Inactif</Badge>}</td>
                  {peut('prix.ecrire') && <td><Bouton compact variante="fantome" className="btn-icone" aria-label={`Modifier ${t.code}`} onClick={() => setEdite(t)}><Pencil aria-hidden /></Bouton></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <DialogueTaux taux={edite} fermer={() => setEdite(null)} />
    </Panneau>
  );
}

function DialogueTaux({ taux, fermer }: { taux: Taux | null | 'nouveau'; fermer: () => void }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const ouvert = taux !== null;
  const ex = taux && taux !== 'nouveau' ? taux : null;
  const [f, setF] = useState({ code: '', label: '', hourlyCost: '', hourlyPrice: '', active: true });
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => {
    if (!ouvert) return;
    setErreur(null);
    setF(ex ? { code: ex.code, label: ex.label, hourlyCost: versSaisie(ex.hourly_cost), hourlyPrice: versSaisie(ex.hourly_price), active: ex.active }
      : { code: '', label: '', hourlyCost: '', hourlyPrice: '', active: true });
  }, [ouvert, ex]);
  const m = useMutation({
    mutationFn: () => {
      const corps = { code: f.code, label: f.label, hourlyCost: f.hourlyCost.trim() || null, hourlyPrice: f.hourlyPrice.trim() || null, active: f.active };
      return ex ? api.patch(`/api/taux-horaires/${ex.id}`, corps) : api.post('/api/taux-horaires', corps);
    },
    onSuccess: () => { notifier(ex ? 'Taux modifié.' : 'Taux ajouté.'); void qc.invalidateQueries({ queryKey: ['taux-horaires'] }); void qc.invalidateQueries({ queryKey: ['revision'] }); fermer(); },
    onError: (e) => setErreur(messageErreur(e)),
  });
  const ch = (k: 'code' | 'label' | 'hourlyCost' | 'hourlyPrice') => ({ value: f[k], onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }) });
  return (
    <Dialogue titre={ex ? `Modifier le taux ${ex.code}` : 'Ajouter un taux horaire'} ouvert={ouvert} fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" type="submit" form="form-taux" chargement={m.isPending}>Enregistrer</Bouton></>}>
      <form id="form-taux" className="pile" onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <div className="grille-2">
          <Champ libelle="Code" mono required maxLength={20} {...ch('code')} aide="Ex. TECH, PROG, MES" />
          <Champ libelle="Libellé" required maxLength={200} {...ch('label')} />
          <Champ libelle="Coût horaire de revient (€)" inputMode="decimal" {...ch('hourlyCost')} aide="Coût interne. Vide = à renseigner." />
          <Champ libelle="Taux de vente indicatif (€ / h)" inputMode="decimal" {...ch('hourlyPrice')} />
        </div>
        <label className="case"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Taux actif (proposé dans les devis)</label>
      </form>
    </Dialogue>
  );
}

// ─── Import fournisseur ───
interface LigneLue { conditionnement: { quantite: string; libelle: string } | null; numero: number; reference: string; label: string; unit: string; prix: string | null; prixBrut: string | null; remise: string | null; erreurs: string[];
  remiseOrigine?: 'fichier' | 'fabricant' | 'globale' | null }
interface Apercu {
  colonnes: string[]; proposition: Record<string, string | undefined>; nbLignes: number; exemple?: { numero: number; valeurs: string[] }[];
  analyse: null | { nbValides: number; nbErreurs: number; erreurs: { ligne: number; reference: string; messages: string[] }[]; lignes: LigneLue[];
    parOrigine?: { fichier: number; fabricant: number; globale: number } };
  /** Option tarification avancée : remises négociées du fournisseur en vigueur à la date du tarif (proposées). */
  remisesNegociees?: RemiseNegociee[];
}
interface Resultat { creees: number; misesAJour: number; inchangees: number; rejetees: number }
const NOUVEAU = '__nouveau__';
const pctFr = (v: string) => `${versSaisie(v)} %`;

function OngletImport({ aller }: { aller: () => void }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const fournisseurs = useFournisseurs();
  const [fichier, setFichier] = useState<File | null>(null);
  const [apercu, setApercu] = useState<Apercu | null>(null);
  const [corr, setCorr] = useState({ reference: '', designation: '', unite: '', prix: '', prixBrut: '', remise: '', fabricant: '', conditionnementQuantite: '', conditionnementLibelle: '' });
  const [choixFournisseur, setChoixFournisseur] = useState('');
  const [nomFournisseur, setNomFournisseur] = useState('');
  const [precision, setPrecision] = useState('');
  const [remise, setRemise] = useState('');
  const [dateTarif, setDateTarif] = useState(dateDuJourParis());
  const [erreur, setErreur] = useState<string | null>(null);
  const [resultat, setResultat] = useState<Resultat | null>(null);
  const [envoi, setEnvoi] = useState(false);
  // Remises par fabricant appliquées à CET import (proposées depuis les remises négociées ; null = pas encore proposées).
  const [remisesFab, setRemisesFab] = useState<{ fabricant: string; remise: string }[] | null>(null);
  const fournisseurOk = (choixFournisseur && choixFournisseur !== NOUVEAU) || (choixFournisseur === NOUVEAU && nomFournisseur.trim());
  useEffect(() => { if (fournisseurs.data && !fournisseurs.data.length && !choixFournisseur) setChoixFournisseur(NOUVEAU); }, [fournisseurs.data, choixFournisseur]);

  const envoyer = async (apercuSeul: boolean, correspondance?: Record<string, string | null>) => {
    if (!fichier) return;
    const fd = new FormData();
    if (correspondance) fd.append('correspondance', JSON.stringify(correspondance));
    fd.append('sourceLabel', precision);
    fd.append('priceDate', dateTarif);
    fd.append('remise', remise);
    if (remisesFab?.length) fd.append('remisesFabricant', JSON.stringify(remisesFab.filter((r) => r.fabricant.trim() && r.remise.trim())));
    if (choixFournisseur === NOUVEAU) fd.append('fournisseurNom', nomFournisseur.trim()); else fd.append('fournisseurId', choixFournisseur);
    fd.append('fichier', fichier);
    setEnvoi(true); setErreur(null);
    try {
      if (apercuSeul) {
        const a = await api.envoyer<Apercu>('/api/prix/import?apercu=1', fd);
        setApercu(a);
        // Remises négociées proposées une fois (la saisie de l'utilisateur prime ensuite).
        if (a.remisesNegociees && remisesFab === null) {
          setRemisesFab(a.remisesNegociees.filter((r) => r.fabricant).map((r) => ({ fabricant: r.fabricant!, remise: versSaisie(r.remise) })));
          const defaut = a.remisesNegociees.find((r) => r.fabricant === null);
          if (defaut && !remise.trim()) setRemise(versSaisie(defaut.remise));
        }
        if (!correspondance) setCorr({ reference: a.proposition.reference ?? '', designation: a.proposition.designation ?? '', unite: a.proposition.unite ?? '',
          prix: a.proposition.prix ?? '', prixBrut: a.proposition.prixBrut ?? '', remise: a.proposition.remise ?? '', fabricant: a.proposition.fabricant ?? '',
          conditionnementQuantite: a.proposition.conditionnementQuantite ?? '', conditionnementLibelle: a.proposition.conditionnementLibelle ?? '' });
      } else {
        const r = await api.envoyer<Resultat>('/api/prix/import', fd);
        setResultat(r); notifier(`Tarif importé : ${r.creees} créé(s), ${r.misesAJour} mis à jour, ${r.inchangees} inchangé(s).`);
        void qc.invalidateQueries({ queryKey: ['prix'] }); void qc.invalidateQueries({ queryKey: ['fournisseurs'] });
        void qc.invalidateQueries({ queryKey: ['imports-tarifs'] }); void qc.invalidateQueries({ queryKey: ['prix-reference'] });
      }
    } catch (x) { setErreur(messageErreur(x)); } finally { setEnvoi(false); }
  };
  const opts = (apercu?.colonnes ?? []).map((c) => ({ valeur: c, libelle: c }));
  const pret = corr.reference && corr.designation && (corr.prix || corr.prixBrut);
  const nettoyee = { reference: corr.reference, designation: corr.designation, prix: corr.prix || null, prixBrut: corr.prixBrut || null,
    remise: corr.remise || null, unite: corr.unite || null, fabricant: corr.fabricant || null, conditionnementQuantite: corr.conditionnementQuantite || null, conditionnementLibelle: corr.conditionnementLibelle || null };
  const choisir = (k: keyof typeof corr) => ({ value: corr[k], onChange: (e: { target: { value: string } }) => setCorr({ ...corr, [k]: e.target.value }) });

  return (
    <div className="pile-l">
      <Panneau titre="1. Fournisseur et fichier du tarif">
        <div className="pile">
          <div className="grille-2">
            <ChampChoix libelle="Fournisseur" vide="Choisir" value={choixFournisseur} onChange={(e) => { setChoixFournisseur(e.target.value); setRemisesFab(null); }}
              options={[...(fournisseurs.data ?? []).map((x) => ({ valeur: x.id, libelle: x.name })), { valeur: NOUVEAU, libelle: 'Nouveau fournisseur…' }]} />
            {choixFournisseur === NOUVEAU && <Champ libelle="Nom du nouveau fournisseur" value={nomFournisseur} maxLength={120} required
              onChange={(e) => setNomFournisseur(e.target.value)} aide="Un même nom n’est jamais créé deux fois (majuscules ignorées)." />}
          </div>
          <p className="petit secondaire">CSV (séparateur point-virgule, virgule ou tabulation) ou classeur XLSX (première feuille), avec une ligne d’en-tête. 10 Mo et 20 000 lignes au plus.
            Les formules ne sont jamais exécutées : seule la valeur enregistrée dans le fichier est lue.</p>
          <label className="btn devis-fichier">
            <FileUp aria-hidden /> {fichier ? fichier.name : 'Choisir un fichier'}
            <input type="file" accept=".csv,.txt,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="devis-fichier-saisie"
              onChange={(e) => { const f = e.target.files?.[0] ?? null; setFichier(f); setApercu(null); setResultat(null); setErreur(null); }} />
          </label>
          {fichier && !apercu && <Bouton variante="principal" chargement={envoi} onClick={() => envoyer(true)}>Lire les colonnes</Bouton>}
          {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        </div>
      </Panneau>
      {apercu && (
        <Panneau titre="2. Colonnes, remise et date du tarif">
          <div className="pile">
            <p className="petit secondaire">{`${pluriel(apercu.nbLignes, 'ligne')} de données détectée(s). Indiquez le prix net, ou le prix brut avec sa remise (colonne de remise, ou remise globale ci-dessous).`}</p>
            <div className="grille-3">
              <ChampChoix libelle="Référence" vide="Choisir" options={opts} {...choisir('reference')} />
              <ChampChoix libelle="Désignation" vide="Choisir" options={opts} {...choisir('designation')} />
              <ChampChoix libelle="Prix net HT" vide="Aucun" options={opts} {...choisir('prix')} />
              <ChampChoix libelle="Prix brut HT" vide="Aucun" options={opts} {...choisir('prixBrut')} />
              <ChampChoix libelle="Remise (%)" vide="Aucune" options={opts} {...choisir('remise')} />
              <ChampChoix libelle="Unité" vide="Aucune (u)" options={opts} {...choisir('unite')} />
              <ChampChoix libelle="Fabricant" vide="Aucun" options={opts} {...choisir('fabricant')} />
              <ChampChoix libelle="Quantité par conditionnement" vide="Aucune" options={opts} {...choisir('conditionnementQuantite')} />
              <ChampChoix libelle="Libellé du conditionnement" vide="Aucun" options={opts} {...choisir('conditionnementLibelle')} />
            </div>
            {apercu.remisesNegociees && remisesFab && <RemisesImport remises={apercu.remisesNegociees} valeurs={remisesFab} changer={setRemisesFab} />}
            <div className="grille-3">
              <Champ libelle="Remise globale (%)" inputMode="decimal" value={remise} onChange={(e) => setRemise(e.target.value)}
                aide="Appliquée au prix brut des lignes sans remise propre. 0 si le prix brut est votre prix d’achat." />
              <Champ libelle="Date du tarif" type="date" value={dateTarif} onChange={(e) => setDateTarif(e.target.value)} required />
              <Champ libelle="Précision (facultatif)" value={precision} maxLength={200} onChange={(e) => setPrecision(e.target.value)} aide="Ex. tarif net octobre 2026" />
            </div>
            <div className="groupe-btn">
              <Bouton variante="principal" disabled={!pret} chargement={envoi} onClick={() => envoyer(true, nettoyee)}>Vérifier les lignes</Bouton>
            </div>
          </div>
        </Panneau>
      )}
      {apercu?.analyse && (
        <Panneau titre="3. Aperçu avant import">
          <div className="pile">
            <div className="rangee">
              <Badge ton="succes">{pluriel(apercu.analyse.nbValides, 'ligne valide', 'lignes valides')}</Badge>
              {apercu.analyse.nbErreurs > 0 && <Badge ton="danger">{pluriel(apercu.analyse.nbErreurs, 'ligne refusée', 'lignes refusées')}</Badge>}
            </div>
            {apercu.analyse.erreurs.length > 0 && (
              <div className="tableau-conteneur">
                <table className="tableau">
                  <caption className="visuellement-cache">Lignes refusées</caption>
                  <thead><tr><th scope="col" className="num">Ligne du fichier</th><th scope="col">Référence</th><th scope="col">Motif</th></tr></thead>
                  <tbody>{apercu.analyse.erreurs.map((e) => (
                    <tr key={e.ligne}><td className="num">{e.ligne}</td><td className="mono">{e.reference || '—'}</td><td>{e.messages.join(' ; ')}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            <div className="tableau-conteneur">
              <table className="tableau">
                <caption className="visuellement-cache">Premières lignes lues</caption>
                <thead><tr><th scope="col" className="num">Ligne</th><th scope="col">Référence</th><th scope="col">Désignation</th><th scope="col">Unité</th>
                  <th scope="col" className="num">Prix net HT</th></tr></thead>
                <tbody>{apercu.analyse.lignes.slice(0, 12).map((l) => (
                  <tr key={l.numero}><td className="num">{l.numero}</td><td className="mono">{l.reference}</td><td>{l.label}<span className="devis-sous petit secondaire">{l.conditionnement ? `${l.conditionnement.libelle} (${versSaisie(l.conditionnement.quantite)} ${l.unit})` : 'Conditionnement inconnu'}</span></td><td>{l.unit}</td>
                    <td className="num">{l.erreurs.length ? <Badge ton="danger">Refusée</Badge> : <>
                      <Montant v={l.prix} />
                      {l.prixBrut && l.remise && <span className="tres-petit discret devis-sous">{`brut ${versSaisie(l.prixBrut)} € − ${pctFr(l.remise)}${l.remiseOrigine === 'fabricant' ? ' (remise négociée du fabricant)' : l.remiseOrigine === 'globale' ? ' (remise globale)' : ''}`}</span>}
                    </>}</td></tr>
                ))}</tbody>
              </table>
            </div>
            {!resultat && (
              <div className="groupe-btn">
                <Bouton variante="principal" icone={<Upload aria-hidden />} disabled={!apercu.analyse.nbValides || !fournisseurOk || !dateTarif} chargement={envoi}
                  onClick={() => envoyer(false, nettoyee)}>{`Importer ${pluriel(apercu.analyse.nbValides, 'ligne')}`}</Bouton>
                {!fournisseurOk && <span className="petit discret">Choisissez le fournisseur du tarif pour importer.</span>}
              </div>
            )}
            {resultat && (
              <Alerte ton="succes" titre="Import terminé" action={<Bouton compact onClick={aller}>Voir les prix</Bouton>}>
                {`${resultat.creees} prix créé(s), ${resultat.misesAJour} mis à jour, ${resultat.inchangees} inchangé(s), ${resultat.rejetees} ligne(s) refusée(s). Les tarifs d’autres dates sont conservés.`}
              </Alerte>
            )}
          </div>
        </Panneau>
      )}
      <ImportsPrecedents />
    </div>
  );
}

interface ImportPasse { id: string; file_name: string; source_label: string | null; price_date: string; discount_pct: string | null; rows_read: number;
  rows_created: number; rows_updated: number; rows_unchanged: number; rows_rejected: number; created_at: string; supplier_name: string; created_by_name: string | null }

function ImportsPrecedents() {
  const r = useQuery({ queryKey: ['imports-tarifs'], queryFn: () => api.get<ImportPasse[]>('/api/prix/imports') });
  if (r.isError) return <EtatErreur erreur={r.error} reessayer={() => r.refetch()} />;
  if (!r.data?.length) return null;
  return (
    <Panneau titre="Imports précédents" serre>
      <ul className="devis-cartes-catalogue">
        {r.data.map((i) => (
          <li key={i.id} className="devis-carte-catalogue">
            <div className="pile">
              <strong>{i.supplier_name}</strong>
              <span className="petit">{`Tarif du ${date(i.price_date)}${i.source_label ? ` — ${i.source_label}` : ''}${i.discount_pct ? ` — remise globale ${pctFr(i.discount_pct)}` : ''}`}</span>
              <span className="tres-petit discret">{`« ${i.file_name} » — importé${i.created_by_name ? ` par ${i.created_by_name}` : ''} le ${dateHeure(i.created_at)}`}</span>
            </div>
            <div className="devis-carte-catalogue-droite tres-petit">
              <span>{`${i.rows_created} créé(s), ${i.rows_updated} mis à jour`}</span>
              <span className="discret">{`${i.rows_unchanged} inchangé(s), ${i.rows_rejected} refusé(s)`}</span>
            </div>
          </li>
        ))}
      </ul>
    </Panneau>
  );
}
